import { T, useT } from "@transifex/react";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback } from "react";
import { type Control, Controller, Form, type FormState, type UseFormClearErrors, useForm } from "react-hook-form";
import { Platform, Pressable } from "react-native";
import type { z } from "zod";

import { useAuthNavigation } from "@/hooks/useAuthNavigation";
import { loginSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";

import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PasswordInput from "@/components/shared/PasswordInput";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAuth } from "@/contexts/AuthContext";
import { removeFromStorage } from "@/utils/secureStore";

type SignInSchemaType = z.infer<typeof loginSchema>;

interface SignInFormProps {
  clearErrors: UseFormClearErrors<SignInSchemaType>;
  control: Control<SignInSchemaType>;
  errors: FormState<SignInSchemaType>["errors"];
  handleSubmit: () => Promise<void>;
  isLoading: boolean;
}

const SignInForm = ({ clearErrors, control, errors, handleSubmit, isLoading }: SignInFormProps) => {
  const t = useT();
  return (
    <Form control={control} onSubmit={handleSubmit}>
      <VStack className="gap-[20px]">
        <FormControl isDisabled={isLoading} isInvalid={!!errors.email} key="email-address-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Email" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.email ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  placeholder={t("Enter Your Email Address")}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  onFocus={() => errors.email && clearErrors("email")}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  returnKeyType="next"
                  autoComplete="email"
                  accessibilityLabel={t("Email Address")}
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.email?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isDisabled={isLoading} isInvalid={!!errors.password} key="password-input" className="gap-[6px]">
          <FormControlLabel>
            <FormControlLabelText className="text-typography-900 text-[14px] leading-[20px] font-semibold align-middle">
              <T _str="Password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                onFocus={() => errors.password && clearErrors("password")}
                error={errors.password?.message}
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.password?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      </VStack>
      {Platform.OS === "web" && <button className="hidden" type="submit" />}
    </Form>
  );
};

const SignIn = () => {
  const { signIn, isLoading, setPendingEmail } = useAuth();
  const router = useRouter();
  const handleAuthNavigation = useAuthNavigation();
  const t = useT();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<SignInSchemaType>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  useFocusEffect(
    useCallback(() => {
      reset();
    }, [reset]),
  );

  const onSubmit = useCallback(
    async (data: SignInSchemaType) => {
      try {
        await signIn(data.email, data.password);
        await removeFromStorage("guestRecentPlates");
        await removeFromStorage("contact");
        handleAuthNavigation();
      } catch (err) {
        if (err instanceof Error && err.name === "UserNotConfirmedException") {
          setPendingEmail({ email: data.email, password: data.password, type: "sign-up" });
          router.replace("/auth/sign-up?verify=true");
        }
      }
    },
    [signIn, handleAuthNavigation, router, setPendingEmail],
  );

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Sign-in")} />

        <SignInForm clearErrors={clearErrors} control={control} errors={errors} handleSubmit={handleSubmit(onSubmit)} isLoading={isLoading} />

        <AppButton title={t("Sign In")} onPress={handleSubmit(onSubmit)} loading={isLoading} icon={ArrowRight} iconPosition="after" />
      </VStack>

      <VStack className="items-center justify-center gap-[16px]">
        <HStack className="justify-center items-center gap-[2px]">
          <Text className="text-[14px] leading-[20px] text-center align-middle  text-typography-900">
            <T _str="Don't have an account? " />
          </Text>

          <Pressable className="active:opacity-70 inline" onPress={() => router.push("/auth/sign-up")}>
            <Text className="text-[14px] font-[700] leading-[20px] underline text-typography-900">
              <T _str="Sign Up." />
            </Text>
          </Pressable>
        </HStack>

        <Pressable className="active:opacity-70 inline text-center" onPress={() => router.push("/auth/forgot-password")}>
          <Text className="text-[14px] font-[700] leading-[20px] underline text-typography-900">
            <T _str="Forgot Password?" />
          </Text>
        </Pressable>
      </VStack>
    </Screen>
  );
};

export default SignIn;
